import { HttpClient } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { SecurityContext } from 'submodules/engine-common/security/security-context';
import { intersect } from '../../../../submodules/common/util';
import { isDesktopApp } from '../../../../submodules/engine-common/environment-util';
import { buildUrl } from '../../../../submodules/engine-common/util';
import { EnvironmentDataService } from './environment-data.service';
import { RestErrorManager } from './errors/rest-error-manager';
import { httpOptions } from './http-util';
import { LocaleDataService } from './locale-data.service';
import { RoutingDataService } from './routing-data.service';
import { log } from './util';


@Injectable()
export class SecurityDataService {

  /**
   * Utente autenticato
   */
  public userProfile: any;

  /**
   * Le authority necessarie per accedere alle pagine private dell'applicazione
   */
  //TODO: dovrebbe essere un array ma sarebbe necessario fare un intersect sul template: ci si può
  // appoggiare a un custom pipe?
  public requiredAuthorities: string;//string[];

  constructor(
    private _injector:Injector,
    private _httpClient: HttpClient,
    private _router: Router,
    private _localeDataService: LocaleDataService,
    private _environmentDataService: EnvironmentDataService) 
  { 
    this.requiredAuthorities = this._environmentDataService.environment.getValue("requiredAuthorities")[0]
  }


  public async signUp(firstName, lastName, username, password, passwordConfirmation, recaptchaToken?){
    await this._httpClient.post(buildUrl(this._environmentDataService.environment.getValue("endpoints.authApi"),
      ["v1","sign-up"]),
      {
      'first-name': firstName,
      'last-name': lastName,
      'username': username,
      'password': password,
      'password-confirmation': passwordConfirmation,
      'recaptcha-token': recaptchaToken
      })
      .toPromise()
  }

  /**
   * Per le desktop app è possibile non passare credenziali, nel caso sia previsto l'autologin 
   * tramite store della pwd sul sistema operativo
   */
  public async login(credentials?){
    if(isDesktopApp()){
      if (this._environmentDataService.environment.getValue("mockUserProfile"))
        await this._mockLoginDesktopApp();
      else
        await this._loginDesktopApp(credentials);
    } else {
      if (this._environmentDataService.environment.getValue("mockUserProfile"))
        await this._mockLoginWebApp();
      else if(this._environmentDataService.environment.getValue("federatedLogin"))
        await this._federatedLoginWebApp();
      else
        await this._loginWebApp(credentials);
    }
  }

  public async logout() {
    if(this._environmentDataService.environment.getValue("mockUserProfile"))
      await this._mockLogout();
    else if(isDesktopApp())
      await this._logoutDesktopApp()
    else {
      try {
          if(this._environmentDataService.environment.getValue("federatedLogin"))
          await this._federatedLogoutWebApp();
        else
          await this._logoutWebApp();
      } catch (error) {
        this._injector.get(RestErrorManager).manageError(error);
      }
    }
  }

  public cookiePolicyAccepted() {
      return (
        !this._environmentDataService.environment.getValue("cookiesConsentRequired")
        || this._injector.get(RoutingDataService).currentRoutingNodeId == 'set_password'
        || this._injector.get(CookieService).get('cookieconsent_status')
      )
  }

  public acceptCookiePolicy() { 
    this._injector.get(CookieService).set('cookieconsent_status',"true",{expires:this._environmentDataService.environment.getValue("cookieConsentExpirationInDay"),domain:this._environmentDataService.environment.getValue("domain"),path:"/"});
    
    const memorizedUrl = localStorage.getItem('redirectUrl');
    localStorage.removeItem('redirectUrl');

    if(memorizedUrl){
      const memorizedUrlFragments = memorizedUrl.split('?');
      
      //Elimina il frammento vuoto prima di /
      const navigateUrl = memorizedUrlFragments[0].split('/').filter(v => v != '');

      //sono presenti parametri 
      if(memorizedUrlFragments.length>1){
          const params = {}
          const rawParams = memorizedUrlFragments[1].split('&');

          rawParams.forEach(element => {
              const fragments = element.split('=')
              params[fragments[0]]=fragments[1]
          });

          this._router.navigate(navigateUrl,{queryParams:params});
      }else{
        this._router.navigate(navigateUrl);
      }
    }
     
  }

  public async revokeCookiePolicyConsent() { 
    this._injector.get(CookieService).delete('cookieconsent_status',"/",this._environmentDataService.environment.getValue("domain"));
    await this.logout()
  }

  /**
   * Per le webApp è autenticato se è presente un accessToken nel local storage.
   * Per le desktopApp si verifica direttamente la presenza dello userProfile in memoria
   */
  public isAuthenticated() { 
    let result;
    if(this._environmentDataService.environment.getValue("mockUserProfile"))
      result = this.userProfile!=null;
    else if(isDesktopApp())
      result = this.userProfile!=null;
    else {
      result = localStorage.getItem('access_token') != null
    }
    // log('SecurityDataService', 'isAuthenticated()', "outcome", result, 'red')
    return result;
  }

  /**
   * Verifica che lo userProfile abbia le authorities specificate in input
   */
  public isAuthorized(requiredAuthorities: string[]) {
    const result = !requiredAuthorities||requiredAuthorities.length==0 ? true : intersect(requiredAuthorities,this.userProfile.authorities).length==requiredAuthorities.length;
    log('SecurityDataService', 'isAuthorized()', "outcome", result, 'orange')
    return result; 
  }

  /**
   * Invia un otp tramite email associata allo username
   * @param username 
   */
  public async requirePasswordReset(username: string, application:string) {
    //TODO: implementare chiamata ad endpoint /auth/api/v1/require-password-reset
    //restiuisce 
    await this._httpClient.post(buildUrl(this._environmentDataService.environment.getValue("endpoints.authApi"),
      ["v1", "require-password-reset"]),
      {
        'username': username,
        'application': application,
        'lc': this._injector.get(LocaleDataService).locale
      })
      .toPromise()
  }

  /**
 * Invia un otp tramite email associata allo username
 * @param username 
 */
  public async requirePasswordSetting(username: string, application:string) {
    //TODO: implementare chiamata ad endpoint /auth/api/v1/require-password-reset
    //restiuisce 
    await this._httpClient.post(buildUrl(this._environmentDataService.environment.getValue("endpoints.authApi"),
      ["v1", "require-password-setting"],
      null),
      {
        'username': username,
        'application': application,
        'lc': this._injector.get(LocaleDataService).locale
      })
      .toPromise()
  }

  /**
   * 
   * @param otp Check if otp is present
   */
  public async checkOtp(otp: string) {
    return await this._httpClient.get(buildUrl(this._environmentDataService.environment.getValue("endpoints.authApi"),
      ["v1", "check-manage-password-otp"],
      [`otp=${otp}`])
     )
      .toPromise()
  }

  /**
   * Invia un otp tramite email associata allo username
   * @param username 
   */
  public async passwordReset(otp: string, password: string, passwordConfirmation: string) {
    //TODO: implementare chiamata ad endpoint /auth/api/v1/require-password-reset
    //restiuisce 
    await this._httpClient.post(buildUrl(this._environmentDataService.environment.getValue("endpoints.authApi"),
      ["v1", "reset-password"],
      null),
      {
        'otp': otp,
        'password': password,
        'password-confirmation': passwordConfirmation
      })
      .toPromise()
  }

  /**
    * Invia un otp tramite email associata allo username
    * @param username 
    */
  public async setPassword(otp: string, password: string, passwordConfirmation: string) {
    //TODO: implementare chiamata ad endpoint /auth/api/v1/require-password-reset
    //restiuisce 
    await this._httpClient.post(buildUrl(this._environmentDataService.environment.getValue("endpoints.authApi"),
      ["v1", "set-password"],
      null),
      {
        'otp': otp,
        'password': password,
        'password-confirmation': passwordConfirmation
      })
      .toPromise()
  }

  /**
   * Naviga verso la url privata richiesta.
   * Quando viene richiesta una specifica url privata e non si è ancora autenticati, questa viene memorizzata nel localStorage e vi si viene 
   * mandati solo a login avvenuto con successo.
   * Per riassumere, dopo un login avvenuto con successo:
   * - In caso di login manuale si viene mandati sulla home page dell'area privata
   * - In caso di login implicita a seguito della richiesta di una pagina privata specifica (per es. segnalibro) si viene inviati sulla pagina specifica
   * @deprecated poco pratico da utilizzare, almeno in caso di login non federato.
   *             Va "incastrato" con i return true/false del verifier
   */
  public async navigateAfterLogin() {
    const memorizedUrl = localStorage.getItem('redirectUrl');
    localStorage.removeItem('redirectUrl');

    //Routing verso pagina senza parametro code che deve essere welcome page dell'area privata
    const urlToNavigate = memorizedUrl ? memorizedUrl.split('/').filter(v => v != '') : ['/private']

    this._router.navigate(urlToNavigate);
  }

  /**
   * Valorizza this.userProfile con i dati aggiornati.
   * Se il profile è già stato ottenuto dal server, allora viene passato in input,
   * altrimenti viene recuperato dal server
   */
  public async setPrincipal(userProfile?: SecurityContext) {
    if(userProfile)
      this.userProfile = userProfile
    else {
      try {
        this.userProfile = await this._httpClient.get(buildUrl(this._environmentDataService.environment.getValue("endpoints.authApi"),
                                                               ["secure","v1","security-context"],
                                                               [`lc=${this._localeDataService.locale}`]),
                                                      httpOptions())
                                                      .toPromise();
      } catch (error) {
        this._injector.get(RestErrorManager).manageError(error);
      }
    }
  }

  public async getSecurityContext() {
    await this._httpClient.get(buildUrl(this._environmentDataService.environment.getValue("endpoints.authApi"),
                                        ["secure","v1","security-context"],
                                        [`lc=${this._localeDataService.locale}`]),
                               httpOptions())
                               .toPromise();
  }

  public async keepSessionAlive() {
    try {
      await this._httpClient.post(buildUrl(this._environmentDataService.environment.getValue("endpoints.authApi"),
        ["secure", "v1", "keep-session-alive"],
        [`lc=${this._localeDataService.locale}`]),
        null,
        httpOptions())
        .toPromise();
    } catch (error) {
      this._injector.get(RestErrorManager).manageError(error);
    }
  }

  public async keepSessionAliveAndMarkAttendance() {
    try {
      await this._httpClient.post(buildUrl(this._environmentDataService.environment.getValue("endpoints.authApi"),
                                           ["secure", "v1", "keep-session-alive-and-mark-attendance"],
                                           [`lc=${this._localeDataService.locale}`]),
                                  null,
                                  httpOptions())
                                  .toPromise();
    } catch (error) {
      this._injector.get(RestErrorManager).manageError(error);
    }
  }

  public cleanUserSession(){
    localStorage.removeItem('access_token');
    this.userProfile = null;
  }

  private _getFederatedLoginUrl() {
    return buildUrl(this._environmentDataService.environment.getValue("endpoints.ssoApi"),
                    ["oauth","authorize"],
                    ["response_type=code",
                    `client_id=${this._environmentDataService.environment.getValue("oauth2.clientId")}`,
                    `redirect_uri=${this._getRedirectUrl()}`,
                    `locale=${this._localeDataService.locale}`
                    ]);
  }

  private _getRedirectUrl() {
    return buildUrl(this._environmentDataService.environment.getValue("oauth2.redirectEndpoint"),
                    ["private"],
                    null)
  }

  private async _federatedLoginWebApp() {
    window.location.href = this._getFederatedLoginUrl()
  }

  private async _federatedLogoutWebApp() {
    await this._httpClient.post(buildUrl(this._environmentDataService.environment.getValue("endpoints.ssoProxyApi"),
                                          ["secure","v1","federated-logout"],
                                          null),
                                null
                            ).toPromise()

    this.cleanUserSession();
    
    this._router.navigate(['/']);
  }

  private async _loginWebApp(credentials) {
    try{ 
      const result: SecurityContext = (
        await this._httpClient.post(buildUrl(this._environmentDataService.environment.getValue("endpoints.authApi"),
                                            ["v1","login"]),
                                    {
                                      'username': credentials.username,
                                      'password': credentials.password,
                                      'recaptcha-token': credentials.recaptchaToken
                                    })
                                    .toPromise()) as SecurityContext

      //Memorizziamo il token
      localStorage.setItem("access_token", result.accessToken);

      //Valorizziamo il principal
      await this.setPrincipal(result);

      this._router.navigate(['/private']);

    }catch(error) {
      // deve rimanere nella maschera di login con messaggio di errore
      console.log('error :>> ', error);
      throw error; //Il form di login  mostra il messaggio nella snackbar a fronte di questo errore
    }
  }

  private async _logoutWebApp() {
    try{
      await this._httpClient.post(buildUrl(this._environmentDataService.environment.getValue("endpoints.authApi"),
                                            ["secure","v1","logout"]),
                                  null,
                                  httpOptions())
                                  .toPromise()

      this.cleanUserSession();
      
      this._router.navigate(['/']);
    }catch(error) {
      this._injector.get(RestErrorManager).manageError(error,{callback: ()=> this._router.navigate(['/'])});
    }
  }

  private async _loginDesktopApp(credentials) {
    this.userProfile = this._environmentDataService.ipcRenderer.sendSync('LOGIN', credentials);

    // in caso di autenticazione avvenuta con successo (userProfile != null) naviga verso l'area privata
    if(this.userProfile)
      this._router.navigate(['/private']);
  }

  private async _logoutDesktopApp() {
    this._environmentDataService.ipcRenderer.sendSync('LOGOUT');
    this.userProfile = null;
    this._router.navigate(['/']);
  }

  private async _mockLoginWebApp() {
    console.log('SecurityDataService.mockLoginWebApp()');
    // if(!this._environmentDataService.isServedByExpress)
    //   this.userProfile = environment['mockUserProfile'];
    // else
    //   this.userProfile = (await this._httpClient.get("/conf/mock-user-profile.json").toPromise()) as SecurityContext;
    // this.navigateAfterLogin();
  }

  private async _mockLoginDesktopApp() {
    this.userProfile = this._environmentDataService.ipcRenderer.sendSync('LOAD_MOCK_USER_PROFILE');
    this._router.navigate(['/private']);
  }

  private async _mockLogout() {
    console.log('SecurityDataService.mockLogout()');
    this.userProfile = null;
    this._router.navigate(['/']);
  }

  /**
   * Per il momento si è deciso di farne a meno. Le authorities aggiornate non sarenno disponibili fino al successivo login
   */
  private _startRefreshUserProfileTrigger = () => {
    setInterval(async() => { 
        //check if access_token is present
        if(this.isAuthenticated())
          this.setPrincipal();
    }, this._environmentDataService.environment.getValue("sso.profileRefreshInMs"));
  } 
}